import React, { useEffect, useState, useCallback, useRef } from "react";
import BasicCustomTable from "../../../../component/basicTable";
import {
  orderColumns,
  orderStatusOptions,
} from "../../../../constants/bussiness";
import CustomPopupForm from "../../../../component/customPopupModel";
import axiosInstance from "../../../../configuration/request";
import {
  capitalizeStatus,
  formatServiceType,
} from "../../../../utils/customerHealperFunctions";
import { useSelector } from "react-redux";
import { useSnackbar } from "../../../../component/SnackbarContext";
import { dateFormat } from "../../../../utils/dateFormat";
import { useLocation, useNavigate } from "react-router-dom";
import CustomerChat from "./customerChat";

const OrderManagement = () => {
  const [open, setOpen] = useState(false);
  const [openChat, setOpenChat] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [ordersDetail, setOrdersDetail] = useState([]);
  const [filters, setFilters] = useState([]);
  const { loginDetails } = useSelector((state) => state.counter);
  const storeId = loginDetails?.storeId;
  const showSnackbar = useSnackbar();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const deepLinkHandledRef = useRef("");

  useEffect(() => {
    if (storeId) fetchOrders(page, rowsPerPage);
  }, [filters, storeId]);

  const buildParams = (pageNumber, limit) => {
    const params = {
      store_id: storeId,
      page: pageNumber ,
      limit,
    };

    let hasOrderStatusFilter = false;

    filters.forEach(({ column, value }) => {
      if (column === "date" && value) {
        const date = new Date(value);
        params.start_date = new Date(date.setHours(0, 0, 0, 0)).toISOString();
        params.end_date = new Date(
          date.setHours(23, 59, 59, 999)
        ).toISOString();
      } else if (column === "customer_name" && value) {
        params.customer_name = value.trim();
      } else if (column === "order_status") {
        hasOrderStatusFilter = true;
        if (value?.length) {
          params.order_status = value.join(",");
        } else {
          hasOrderStatusFilter = false;
        }
      }
    });
    if (!hasOrderStatusFilter) {
      params.order_status = orderStatusOptions.join(",");
    }

    return params;
  };

  const fetchOrders = useCallback(
    (pageNumber = 1, limit = 10) => {
      const params = buildParams(pageNumber, limit);
      axiosInstance
        .get(`/customer/orders`, { params })
        .then((res) => {
          const mappedOrders = res?.data?.data?.map((order) => {
            const address = order?.user_address
              ? [
                  order?.user_address.address_line,
                  order?.user_address.landmark,
                  order?.user_address.city,
                  order?.user_address.state,
                  order?.user_address.pincode,
                ]
                  .filter(Boolean)
                  .join(", ")
              : "N/A";

            return {
              id: order?.id,
              order_id: order?.order_id,
              order_status: order?.status,
              date: dateFormat(order?.created_at) || "",
              address,
              contact: order?.user?.phone_number,
              customer_name: order?.user?.name,
              customer_img: order?.user?.profile_img_url,
              service_type: order?.services?.service_type,
              service: formatServiceType(order?.services?.service_type),
              delivery_time:
                order?.delivery_time_slot?.close +
                "-" +
                order?.delivery_time_slot?.close,
              delivery_date: dateFormat(order?.deliveryDate) || "",
              totalPrice: parseInt(order?.total_amount),
              totalQuantity:
                order?.services?.items?.reduce(
                  (acc, item) => acc + (item.quantity || 0),
                  0
                ) || 0,
              items: order?.services?.items,
              additionalItems: order?.additionalItems || [],
              laundryNotes: order?.laundryNotes,
              isMismatchExist: order?.mismatch ? true : false,
              canUpdateStatus: [
                // "Picked Up",
                "At Store",
                "Processing",
                "Delayed",
                "Ready for Delivery",
              ].includes(order?.status),
              breakdown: order?.breakdown || {},
            };
          });
          setOrdersDetail(mappedOrders);
          setPage(res?.data?.meta?.page || 0);
          setTotalCount(res?.data?.meta?.total || 0);
        })
        .catch((error) => {
           showSnackbar(error?.response?.data?.message || "Something went wrong!", "error");
        });
    },
    [buildParams, showSnackbar]
  );

  const handlePageChangeRequest = (newPage, newRowsPerPage) => {
    setPage(newPage);
    setRowsPerPage(newRowsPerPage);
    fetchOrders(newPage, newRowsPerPage);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleView = (row) => {
    //  navigate(`/app/order-management/view/${row.id}`);
    setOpen(true);
    setSelectedRow(row);
  };

  const handleChat = (row) => {
    setOpenChat(true);
    setSelectedRow(row);
  };

  const filterHandler = (filter) => {
    setFilters((prev) => {
      const filtered = prev.filter((f) => f.column !== filter.column);
      return [...filtered, filter];
    });
  };
  const handleSubmit = (row) => {
    setOpen(false);
    setOpenChat(true);
    setSelectedRow(row);
  };

  const handleTagChange = (newStatus, rowData) => {
    if (!rowData.canUpdateStatus) {
      showSnackbar("You are not allow to change status for now", "info");
      return;
    }
    const payload = {
      orderStatus: newStatus,
    };
    axiosInstance
      .patch(`/customer/orders/status?orderId=${rowData?.id}`, payload)
      .then(() => {
        fetchOrders();
      })
      .catch((error) => {
           showSnackbar(error?.response?.data?.message || "Something went wrong!", "error");
       
      });
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const orderIdParam = searchParams.get("orderId");
    const chatParam = searchParams.get("chat");

    if (!orderIdParam || !ordersDetail.length) {
      return;
    }

    const deepLinkKey = `${orderIdParam}:${chatParam || ""}`;
    if (deepLinkHandledRef.current === deepLinkKey) {
      return;
    }

    const matchedOrder = ordersDetail.find(
      (order) =>
        String(order?.id) === String(orderIdParam) ||
        String(order?.order_id) === String(orderIdParam)
    );

    if (!matchedOrder) {
      return;
    }

    deepLinkHandledRef.current = deepLinkKey;
    setSelectedRow(matchedOrder);
    setOpen(true);
    if (chatParam === "store") {
      setOpenChat(true);
    }

    navigate(location.pathname, { replace: true });
  }, [location.pathname, location.search, navigate, ordersDetail]);

  return (
    <div className="pl-6 pr-4">
      <h2 className="text-2xl font-semibold mb-2 mt-1 ml-3">
        Order Management
      </h2>
      <div className="px-2 pt-1 pb-4 h-[calc(100vh-145px)]">
        <BasicCustomTable
          columns={orderColumns}
          rows={ordersDetail}
          onView={handleView}
          onChat={handleChat}
          colorHeader={true}
          customClassHeader="table-header-cell-colored"
          tableName="order management"
          enableCheckbox={true}
          handleFilter={filterHandler}
          maxHeightClass="max-h-[calc(100vh-230px)]"
          backendPagination={true}
          totalCount={totalCount}
          onPageChangeRequest={handlePageChangeRequest}
          pageLimit={rowsPerPage}
          page={page}
          onTagChange={handleTagChange}
        />
      </div>
      <CustomPopupForm
        isOpen={open}
        handleClose={handleClose}
        dialogType="order-view"
        dialogDetails={selectedRow}
        onSubmit={handleSubmit}
      />
      {openChat && (
        <div className="fixed bottom-4 right-4 w-[90%] max-w-sm z-50">
          <CustomerChat
            onClose={() => setOpenChat(false)}
            order_id={selectedRow?.id}
            customer_name={selectedRow?.customer_name} 
            customer_img={selectedRow?.customer_img}
            orderId={selectedRow?.order_id}
          />
        </div>
      )}
    </div>
  );
};

export default OrderManagement;
